import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * The status colour code shared by every table:
 *   success   green    running, working          (Active)
 *   info      blue     in progress at the site   (In review)
 *   warning   amber    needs attention soon      (Expiring soon, Pending settlement)
 *   error     red      broken or blocked         (Needs reconnect, Disapproved, Disabled)
 *   paused    purple   paused on purpose         (Paused, Campaign paused)
 *   archived  slate    put away                  (Archived, Closed)
 *   deleted   magenta  removed                   (Deleted)
 *   neutral   grey     anything unmapped
 * Dots pass the dataviz palette checks (every pair distinct with normal vision); badge text is at least 4.5:1 on its
 * tint. Colour is never the only cue: the label is always shown.
 */
export const TONES = ['success', 'info', 'warning', 'error', 'paused', 'archived', 'deleted', 'neutral'];

// Fixed tones from the sponsortrialx palette (DeepPurple, naturalGrey / darkestWhite / infoAlertBg)
const FIXED_TONES = {
  paused: { dot: '#880ED4', text: '#6E0BAD', bg: '#F5E5FE' },
  archived: { dot: '#94A3B8', text: '#475569', bg: '#F1F5F9' },
  deleted: { dot: '#D6409F', text: '#A3246F', bg: '#FDE8F3' },
};

function toneColors(theme, tone) {
  if (FIXED_TONES[tone]) return FIXED_TONES[tone];
  if (tone === 'neutral' || !theme.palette[tone]) {
    return { dot: theme.palette.grey[500], text: theme.palette.text.secondary, bg: theme.palette.grey[100] };
  }
  const palette = theme.palette[tone];
  return { dot: palette.main, text: palette.dark, bg: alpha(palette.main, 0.1) };
}

export function StatusDot({ tone, size = 8 }) {
  return (
    <Box component="span" aria-hidden
         sx={(theme) => ({ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
           bgcolor: toneColors(theme, tone).dot })} />
  );
}

// title, when given, explains the status on hover
export default function StatusBadge({ tone = 'neutral', label, title }) {
  const badge = (
    <Box component="span" sx={(theme) => {
      const colors = toneColors(theme, tone);
      return {
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: 4, fontSize: 12.5,
        fontWeight: 500, lineHeight: 1.6, whiteSpace: 'nowrap', color: colors.text, bgcolor: colors.bg,
      };
    }}>
      <StatusDot tone={tone} size={7} />
      {label}
    </Box>
  );
  return title ? <Tooltip title={title}>{badge}</Tooltip> : badge;
}
